import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Route, Routes } from 'react-router';
import { Header, Footer } from '@ui/siteStructure';
import { SidebarAds } from '@ui/ads/components/SidebarAds';
import { DeviceType } from '@repo/types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CmsPage } from './pages/CmsPage';
import { ArticlePage } from './pages/ArticlePage';
import { FixturesPage } from './pages/FixturesPage';
import { ResultsPage } from './pages/ResultsPage';
import { TeamPage } from './pages/TeamPage';
import { MatchPage } from './pages/MatchPage/MatchPage';
import { NewsletterConfirm } from './pages/NewsletterConfirm';
import { useScrollToTop } from './utils/useScrollToTop';
import { ScrollToTop } from '@ui/common/components/ScrollToTop/ScrollToTop';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { ads } from './constants';
import './styles/index.css';
import './styles/theme.css';
import './styles/breakpoints.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { ClubPage } from './pages/ClubPage';
// import * as Sentry from '@sentry/react';

// Sentry.init({
//   dsn: 'https://867a5f5150ce3ed79b4bb0d5d5268638@o4509271836459009.ingest.de.sentry.io/4509271837966416',
//   // Setting this option to true will send default PII data to Sentry.
//   // For example, automatic IP address collection on events
//   sendDefaultPii: true,
//   integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
//   // Tracing
//   tracesSampleRate: 1.0, //  Capture 100% of the transactions
//   // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
//   tracePropagationTargets: [
//     'localhost',
//     /^https:\/\/api.isthmian\.co\.uk/,
//     /^https:\/\/api.thenpl\.co\.uk/,
//     /^https:\/\/api.grimsbyboroughfc\.com/,
//   ],
//   // Session Replay
//   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
//   replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
// });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

export function ClientApp() {
  const deviceType = useDeviceType();
  useScrollToTop();

  return (
    <HelmetProvider>
      <Helmet prioritizeSeoTags>
        <title>{import.meta.env.VITE_SITE_NAME}</title>
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
        <meta name="twitter:site" content={import.meta.env.VITE_TWITTER_SITE} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <div className="page-wrapper">
          <Header />
          <div className="main-content-wrapper">
            <main>
              <section className="leaderboard-ad-container">
                <div className="leaderboard-ad" id="snack-top-slim" />
              </section>
              <Routes>
                <Route path="/match/:id" element={<MatchPage />} />
                <Route path="/clubs/:slug" element={<ClubPage />} />
                <Route path="/teams/:slug" element={<TeamPage />} />
                <Route path="/articles/:slug" element={<ArticlePage />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/fixtures" element={<FixturesPage />} />
                <Route path="/newsletter-confirm" element={<NewsletterConfirm />} />
                <Route path="*" element={<CmsPage />} />
              </Routes>
              <ScrollToTop />
            </main>
            {deviceType === DeviceType.desktop ? <SidebarAds ads={ads} /> : <div id="snack-vod" />}
          </div>
          <Footer />
        </div>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export function ServerApp() {
  return (
    <Helmet prioritizeSeoTags>
      <title>{import.meta.env.VITE_SITE_NAME}</title>
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
      {import.meta.env.VITE_TWITTER_SITE && <meta name="twitter:site" content={import.meta.env.VITE_TWITTER_SITE} />}
      {import.meta.env.VITE_TWITTER_SITE && <meta name="twitter:card" content="summary_large_image" />}
    </Helmet>
  );
}
